import {
	DataTypeProvider,
	GroupingState,
	IntegratedGrouping,
	IntegratedSorting,
	Sorting,
	SortingState,
	TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
	Grid,
	TableColumnReordering,
	TableColumnResizing,
	TableGroupRow,
	TableHeaderRow,
	VirtualTable,
} from '@devexpress/dx-react-grid-bootstrap4';
import { IPredictionGroupingDto } from 'api/api';
import { Guid } from 'guid-typescript';
import moment from 'moment';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOrder, setSize, setSorting } from 'state/ducks/prediction-table-settings/operations';
import {
	predictionAmountOfDaysColumns,
	PredictionColumns,
	predictionDateColumns,
	PredictionGroupingDtoKeys,
} from 'state/ducks/prediction-table-settings/types';
import { SortingGeneric, TableColumnWidthInfoGeneric } from 'state/ducks/table-settings/types';
import { localized, localizedInterpolation } from 'state/i18n';
import { AppState } from 'state/store';
import { DateFormatterModes, getDateToString } from 'view/shared/components/date-to-string';
import ProgressBar from 'view/shared/components/progress-bar/progress-bar';
import { ROW_HEIGHT } from './bi-table';

interface Props {
	data: IPredictionGroupingDto[];
}

const rootStyle: React.CSSProperties = { height: '100%' };
const Root = (props: Grid.RootProps) => (
	<Grid.Root className="devextreme-react-grid-remove-margin-bottom" {...props} style={rootStyle} />
);

const TableComponent = ({ ...restProps }) => <VirtualTable.Table {...restProps} className="bi-report-table-striped" />;

const HeaderComponent = ({ ...restProps }) => <VirtualTable.TableHead {...restProps} className="table-header" />;

const TableRow = (obj: any) => <VirtualTable.Row {...obj} />;

const getRowId = (row: any) => {
	return Guid.create().toString();
};

const PredictionTable: FC<Props> = memo(props => {
	const { data } = props;
	const dispatch = useDispatch();
	const sorting = useSelector((state: AppState) => state.predictionTableSettingsReducer.sorting);
	const columnOrder = useSelector((state: AppState) => state.predictionTableSettingsReducer.columnOrder);
	const columnWidths = useSelector((state: AppState) => state.predictionTableSettingsReducer.columnWidths);
	const columnsAll = useSelector((state: AppState) => state.predictionTableSettingsReducer.columns);

	const rows = useMemo(
		() =>
			data
				.flatMap(group =>
					group?.predictions?.map(
						(prediction): PredictionColumns => {
							const predictedDate = moment(prediction.predictionDate).add(
								prediction.predictedMinutesUntilFull,
								'minutes'
							);
							return {
								...prediction,
								predictedDaysUntilFull: prediction.predictedMinutesUntilFull / (60 * 24),
								actualEmptiedDate: group.actualEmptiedDate ?? new Date(1970), //Trick to make sorting work with undefined
								predictedDate: predictedDate.toDate(),
								innerCollapsible: {
									actualEmptiedDate: group.actualEmptiedDate,
									fillLevelWhenEmptied: group.fillLevelWhenEmptied,
								},
								outerCollapsible: {
									actualEmptiedDate: group.actualEmptiedDate,
									fillLevelWhenEmptied: group.fillLevelWhenEmptied,
									serialNumber: group.serialNumber,
								},
								predictedFillLevel: (() => {
									if (!predictedDate || !group.previousEmptyingDate || !prediction.predictionDate) {
										return undefined;
									}

									const startDate = moment(group.previousEmptyingDate);
									const endDate = moment(predictedDate);
									const predictionDate = moment(prediction.predictionDate);

									const totalDuration = endDate.diff(startDate, 'minutes');
									const elapsedDuration = predictionDate.diff(startDate, 'minutes');

									if (totalDuration <= 0) {
										// If we are already past the end date, progress is 100%, otherwise 0%
										return predictionDate.isAfter(endDate) ? 100 : 0;
									}

									const progress = (elapsedDuration / totalDuration) * 100;

									return Math.round(Math.max(0, Math.min(progress, 100)));
								})(),
								predictionError: group.actualEmptiedDate
									? Math.abs(moment(group.actualEmptiedDate).diff(predictedDate, 'days', true))
									: undefined,
							};
						}
					)
				)
				.filter(r => r !== undefined),
		[data]
	);

	const DateFormatter = useCallback(
		(obj: { value?: Date }) =>
			obj.value ? (
				<span>{getDateToString(obj.value, DateFormatterModes.withMinutes)}</span>
			) : (
				<span>{localized('NoData')}</span>
			),
		[]
	);

	const DaysFormatter = useCallback(
		(obj: { value?: number }) =>
			obj.value ? (
				<span>
					{obj.value.toFixed(2)} {localized('days')}
				</span>
			) : (
				<span>{localized('NoData')}</span>
			),
		[]
	);

	const FillLevelFormatter = useCallback(
		(obj: { value?: number }) =>
			obj.value ? <ProgressBar value={obj.value} maxValue={100} /> : <span>{localized('NoData')}</span>,
		[]
	);

	const changeColumnWidths = useCallback(
		(columnWidths: TableColumnWidthInfo[]) =>
			dispatch(setSize(columnWidths as TableColumnWidthInfoGeneric<PredictionGroupingDtoKeys>[])),
		[dispatch]
	);

	const changeSorting = useCallback(
		(sorting: Sorting[]) => dispatch(setSorting(sorting as SortingGeneric<PredictionGroupingDtoKeys>[])),
		[dispatch]
	);

	const changeColumnOrder = useCallback(
		(order: string[]) => dispatch(setOrder(order as PredictionGroupingDtoKeys[])),
		[dispatch]
	);

	const outerGroupingCriteria = useCallback(value => {
		if (value && 'actualEmptiedDate' in value && 'fillLevelWhenEmptied' in value && 'serialNumber' in value) {
			if (!value.actualEmptiedDate)
				return {
					key: value.serialNumber,
					value: localizedInterpolation('NoDataForMachineInt', { machine: value.serialNumber }),
				};
			const datePart = new Date(value.actualEmptiedDate).toLocaleDateString();
			const fillLevelPart = `${value.fillLevelWhenEmptied}%`;
			return {
				key: value.serialNumber,
				value: `${value.serialNumber}| ${localizedInterpolation('LastEmptiedAtIntWithIntFill', {
					date: datePart,
					fill: fillLevelPart,
				})}`,
			};
		}
	}, []);

	const innerGroupingCriteria = useCallback(value => {
		if (value && 'actualEmptiedDate' in value && 'fillLevelWhenEmptied' in value) {
			if (!value.actualEmptiedDate) return { key: localized('NotEmptiedYet') };
			const datePart = new Date(value.actualEmptiedDate).toLocaleDateString();
			const fillLevelPart = `${value.fillLevelWhenEmptied}%`;
			return {
				key: `${datePart}`,
				value: localizedInterpolation('EmptiedAtIntWithIntFill', {
					date: datePart,
					fill: fillLevelPart,
				}),
			};
		}
		return value;
	}, []);

	return (
		<Grid rows={rows} columns={columnsAll} getRowId={getRowId} rootComponent={Root}>
			<SortingState sorting={sorting} onSortingChange={changeSorting} />
			<IntegratedSorting />
			<GroupingState
				defaultGrouping={[{ columnName: 'outerCollapsible' }, { columnName: 'innerCollapsible' }]}
				defaultExpandedGroups={[]}
			/>
			<IntegratedGrouping
				columnExtensions={[
					{ columnName: 'outerCollapsible', criteria: outerGroupingCriteria },
					{ columnName: 'innerCollapsible', criteria: innerGroupingCriteria },
				]}
			/>
			<DataTypeProvider for={predictionDateColumns} formatterComponent={DateFormatter} />
			<DataTypeProvider for={predictionAmountOfDaysColumns} formatterComponent={DaysFormatter} />
			<DataTypeProvider for={['predictedFillLevel']} formatterComponent={FillLevelFormatter} />
			<VirtualTable
				tableComponent={TableComponent}
				headComponent={HeaderComponent}
				height="auto"
				columnExtensions={columnWidths}
				rowComponent={TableRow}
				estimatedRowHeight={ROW_HEIGHT}
			/>
			<TableColumnReordering onOrderChange={changeColumnOrder} order={columnOrder} />
			<TableColumnResizing onColumnWidthsChange={changeColumnWidths} columnWidths={columnWidths} />
			<TableGroupRow />
			<TableHeaderRow />
		</Grid>
	);
});

export default PredictionTable;
